<template>
  <div class="adv-wrap">
    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">← بازگشت</button>
      <h1 class="title">{{ storyTitle || 'داستان تعاملی' }} 📖</h1>
      <div class="status-badge" :class="{ 'thinking': waiting }">
        {{ waiting ? 'داره فکر می‌کنه...' : 'آماده قصه‌گویی' }}
      </div>
    </div>

    <!-- Chat Area -->
    <div class="chat-container">
      <div class="chat-messages" ref="chatRef">
        <!-- Messages -->
        <div 
          v-for="m in messages" 
          :key="m.id" 
          class="msg-row" 
          :class="m.role"
        >
          <div class="avatar">
            <template v-if="m.role==='kid'">🧒</template>
            <template v-else-if="m.role==='ai'">🤖</template>
            <template v-else>✨</template>
          </div>
          <div class="bubble">
            <div class="bubble-content">{{ m.text }}</div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="waiting" class="msg-row ai typing">
          <div class="avatar">🤖</div>
          <div class="bubble">
            <div class="typing-dots">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="input-area">
        <button 
          class="mic-btn" 
          :class="{ 'listening': listening, 'disabled': waiting }" 
          @click="toggleVoice"
          :disabled="waiting"
        >
          <div class="mic-icon">{{ listening ? '⏹️' : '🎙️' }}</div>
          <div class="mic-waves" v-if="listening"></div>
        </button>
        
        <div class="text-input-wrap">
          <input 
            class="text-input" 
            v-model="inputText" 
            @keyup.enter="send" 
            placeholder="چی بگم؟..." 
            :disabled="waiting"
            ref="inputRef"
          />
        </div>

        <button 
          class="send-btn" 
          @click="send" 
          :disabled="!inputText.trim() || waiting"
        >
          <span class="btn-text">ارسال</span>
          <span class="btn-icon">✈️</span>
        </button>
      </div>
    </div>

    <!-- Background Decorations -->
    <div class="bg-decoration tree-1">🌳</div>
    <div class="bg-decoration tree-2">🌲</div>
    <div class="bg-decoration bird">🐦</div>
  </div>
</template>

<script>
export default {
  name: 'AdventureGame'
}
</script>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation } from '@vue/apollo-composable'
import { START_STORY_MUTATION, STORY_ASK_MUTATION } from '../api/mutations'

const router = useRouter()
const route = useRoute()
const slug = route.params.slug

const chatRef = ref(null)
const inputRef = ref(null)
const inputText = ref('')
const messages = ref([])
const listening = ref(false)
const waiting = ref(false)
const storySessionId = ref(null)
const storyTitle = ref('')

let rec = null
let mid = 0

const { mutate: startStory } = useMutation(START_STORY_MUTATION)
const { mutate: storyAsk } = useMutation(STORY_ASK_MUTATION)

function goBack() { router.push(`/room/${slug}`) }

function addMessage(role, text) {
  messages.value.push({ id: ++mid, role, text })
  scrollToBottom()
}

function scrollToBottom() {
  nextTick(() => {
    if (chatRef.value) {
      chatRef.value.scrollTop = chatRef.value.scrollHeight
    }
  })
}

async function send() {
  const text = String(inputText.value || '').trim()
  if (!text || waiting.value) return

  addMessage('kid', text)
  inputText.value = ''
  waiting.value = true
  
  try {
    const { data } = await storyAsk({ data: { sessionId: storySessionId.value, text } })
    const reply = data?.storyAsk?.reply || ''
    if (data?.storyAsk?.title) storyTitle.value = data.storyAsk.title
    
    // Safety check: ensure we don't just echo if backend fails to filter
    
    // Safety check: ensure we don't just echo if backend fails to filter
    const cleanReply = reply.replace(text, '').trim() || reply
    
    addMessage('ai', cleanReply)
    speak(cleanReply)
  } catch (e) {
    addMessage('system', 'اوه! ارتباط قطع شد. دوباره تلاش کن.')
  } finally {
    waiting.value = false
    nextTick(() => inputRef.value?.focus())
  }
}

// Voice Recognition
function toggleVoice() {
  if (waiting.value) return
  if (listening.value) {
    stopVoice()
  } else {
    startVoice()
  }
}

function startVoice() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!SR) {
    addMessage('system', 'مرورگر شما از گفتار پشتیبانی نمی‌کند.')
    return
  }
  
  rec = new SR()
  rec.lang = 'fa-IR'
  rec.interimResults = false
  rec.maxAlternatives = 1
  
  listening.value = true
  
  rec.onresult = (evt) => {
    const t = evt.results?.[0]?.[0]?.transcript || ''
    if (t) {
      inputText.value = t
      // Optional: Auto-send
      // send()
    }
  }
  
  rec.onend = () => { listening.value = false }
  rec.onerror = () => { listening.value = false }
  
  try { rec.start() } catch (e) { listening.value = false }
}

function stopVoice() {
  if (rec && rec.stop) rec.stop()
  listening.value = false
}

// Text to Speech
function speak(text) {
  if (!window.speechSynthesis) return
  // Cancel previous speech
  window.speechSynthesis.cancel()
  
  const u = new SpeechSynthesisUtterance(text)
  u.lang = 'fa-IR'
  u.rate = 0.9 // Slightly slower for kids
  window.speechSynthesis.speak(u)
}

onMounted(async () => {
  addMessage('system', 'سلام! من قصه‌گو هستم. بیا با هم یک داستان بسازیم! موضوع داستان رو بگو...')
  
  try {
    const res = await startStory({ data: { roomSlug: slug } })
    storySessionId.value = res?.data?.startStorySession?.sessionId || null
    storyTitle.value = res?.data?.startStorySession?.title || ''
  } catch (e) {
    console.error("Failed to start story session", e)
  }
  
  // Focus input on load
  nextTick(() => inputRef.value?.focus())
})

onBeforeUnmount(() => {
  stopVoice()
  window.speechSynthesis.cancel()
})
</script>

<style scoped>
.adv-wrap {
  min-height: 100vh;
  padding: 16px;
  background: linear-gradient(135deg, #a7f3d0 0%, #6ee7b7 50%, #34d399 100%);
  font-family: 'Vazirmatn', sans-serif;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  z-index: 10;
}

.back-btn {
  background: #fff;
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 700;
  color: #059669;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}
.back-btn:hover { transform: scale(1.05); }

.title {
  flex: 1;
  font-size: 20px;
  margin: 0;
  color: #064e3b;
  text-shadow: 0 2px 4px rgba(255,255,255,0.4);
}

.status-badge {
  background: rgba(255, 255, 255, 0.6);
  padding: 6px 12px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
}
.status-badge.thinking {
  background: #fef08a; /* yellow */
  color: #854d0e;
  animation: pulse 1.5s infinite;
}

.chat-container {
  flex: 1;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0,0,0,0.1);
  z-index: 10;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;
}

.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  max-width: 85%;
}
.msg-row.kid {
  align-self: flex-end;
  flex-direction: row-reverse;
}
.msg-row.ai {
  align-self: flex-start;
}
.msg-row.system {
  align-self: center;
  max-width: 90%;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  border: 2px solid #fff;
}

.bubble {
  padding: 12px 16px;
  border-radius: 20px;
  font-size: 16px;
  line-height: 1.6;
  position: relative;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.msg-row.kid .bubble {
  background: #0ea5e9; /* sky blue */
  color: #fff;
  border-bottom-left-radius: 4px; /* tailor styling */
}

.msg-row.ai .bubble {
  background: #fff;
  color: #334155;
  border-bottom-right-radius: 4px;
}

.msg-row.system .bubble {
  background: #fef9c3; /* light yellow */
  color: #854d0e;
  border-radius: 99px;
  padding: 8px 16px;
  font-size: 14px;
  text-align: center;
}

/* Typing Animation */
.typing-dots {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}
.typing-dots span {
  width: 8px;
  height: 8px;
  background: #cbd5e1;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}
.typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.typing-dots span:nth-child(2) { animation-delay: -0.16s; }

.input-area {
  padding: 16px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 12px;
  align-items: center;
  transition: all 0.3s ease;
}

.mic-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  position: relative;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}
.mic-btn:hover:not(:disabled) { background: #e2e8f0; transform: scale(1.05); }
.mic-btn.listening {
  background: #ef4444;
  color: #fff;
  animation: pulse-red 1.5s infinite;
}
.mic-btn:disabled { opacity: 0.5; cursor: default; }

.text-input-wrap {
  flex: 1;
  position: relative;
  display: flex;
}

.text-input {
  width: 100%;
  padding: 12px 20px;
  border-radius: 99px;
  border: 2px solid #e2e8f0;
  font-size: 16px;
  font-family: inherit;
  outline: none;
  transition: all 0.2s ease;
  background: #f8fafc;
}
.text-input:focus { 
  border-color: #0ea5e9; 
  background: #fff;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}
.text-input:disabled { background: #f1f5f9; cursor: default; }

.send-btn {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: #fff;
  border: none;
  border-radius: 99px;
  padding: 0 24px;
  height: 48px;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}
.send-btn:hover:not(:disabled) { 
  transform: translateY(-2px); 
  box-shadow: 0 6px 16px rgba(14, 165, 233, 0.4); 
}
.send-btn:disabled { 
  opacity: 0.6; 
  cursor: default; 
  box-shadow: none; 
  transform: none; 
  background: #cbd5e1;
}

.btn-icon { font-size: 18px; }

/* Decorations */
.bg-decoration {
  position: absolute;
  font-size: 80px;
  opacity: 0.4;
  pointer-events: none;
  z-index: 1;
}
.tree-1 { bottom: -20px; right: -20px; font-size: 140px; }
.tree-2 { bottom: -10px; left: -10px; font-size: 120px; }
.bird { top: 60px; left: 10%; font-size: 40px; animation: float 10s ease-in-out infinite; }


@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}

@keyframes pulse-red {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

@media (max-width: 600px) {
  .adv-wrap { padding: 0; }
  .header { padding: 16px; margin: 0; }
  .chat-container { border-radius: 24px 24px 0 0; box-shadow: none; flex: 1; height: auto; }
  
  .input-area {
    padding: 12px;
    gap: 8px;
    border-top: 1px solid #f1f5f9;
  }
  
  .send-btn .btn-text { display: none; }
  .send-btn {
    width: 48px;
    padding: 0;
    border-radius: 50%;
  }
}
</style>
